/**
 * Pure mapping from a 0..1 noise level to a classroom-friendly "zone" and its
 * presentation. Kept separate from the component so the thresholds are unit
 * tested and easy to tune. Levels come from useMicLevel (RMS, smoothed,
 * normalized ~0..1). Thresholds are deliberately forgiving — mic sensitivity
 * varies by device — and tuned so a quiet room reads "calm", normal working
 * voices read "good", and a rowdy class tips into "loud".
 */

export type NoiseZone = 'calm' | 'good' | 'loud';

const GOOD_AT = 0.24;
const LOUD_AT = 0.58;

export function levelToZone(level: number): NoiseZone {
  if (level >= LOUD_AT) return 'loud';
  if (level >= GOOD_AT) return 'good';
  return 'calm';
}

export interface ZonePresentation {
  /** Big word shown to the class. */
  label: string;
  emoji: string;
  /** One short, kind line. */
  blurb: string;
}

export const ZONE_META: Record<NoiseZone, ZonePresentation> = {
  calm: { label: 'Calm', emoji: '😌', blurb: 'Lovely and quiet' },
  good: { label: 'Good voices', emoji: '🙂', blurb: 'Nice working volume' },
  loud: { label: 'Too loud', emoji: '🤫', blurb: "Let's bring it down" },
};
